import {
    dropTargetForElements,
    monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ChevronRight, Folder, MoreHorizontal } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { IBreadcrumbSegment, ILibraryDragItem } from "./DocumentPicker.types";

/**
 * Presentational breadcrumb shared across library surfaces — the SEAM
 * (insurescope-7149k.3.1.5). Callers supply `segments` (navigation lives in
 * each segment's `onClick`; this component never touches the router) plus
 * optional `onDrop`/`isValidDrop`, which make every NON-current segment
 * with a defined `folderId` a drop target (see IBreadcrumbSegment.folderId
 * for the null-vs-undefined drop semantics — they are not interchangeable).
 *
 * The two existing builders, for anyone adding a third surface:
 * - FilterBar's internal path-based builder (main Documents/KB library):
 *   router navigation; root crumb "Documents" carries `folderId: null`
 *   (drop = move to the library root).
 * - ClientLibraryTab's `workspaceBreadcrumbs` (client workspace): `?folder=`
 *   param navigation owned by ClientDetail; root crumb = the client name
 *   and carries the client ROOT folder's real id, never null (a null move
 *   would strip the client tag).
 *
 * Overflow: with `maxVisible` set, paths longer than it (and longer than 3)
 * collapse to first + "…" listbox + last two. The listbox is deliberately
 * NON-portalled so a drag can travel trigger → items without an
 * onDragLeave closing the menu mid-drag.
 */
interface PickerBreadcrumbProps {
    segments: IBreadcrumbSegment[];
    /** When set, segments beyond this count collapse into an overflow menu. */
    maxVisible?: number;
    /** Called when items are dropped on a breadcrumb segment. */
    onDrop?: (
        folderId: number | null,
        folderName: string,
        items: ILibraryDragItem[]
    ) => void;
    /** Validates whether a drop is allowed on the given folder. */
    isValidDrop?: (
        folderId: number | null,
        items: ILibraryDragItem[]
    ) => boolean;
}

// Shared drop-target hover treatment — the fourth copper-during-drag surface
// (see Unit 2/3 briefs). Same tokens as `library-row--drop-target` and the
// DragPreview badge so users learn one drop-affordance vocabulary.
const DROP_HOVER_CLASS =
    "bg-brand-copper/[0.06] outline outline-2 outline-brand-copper/40 rounded-md";

// ---------------------------------------------------------------------------
// Shared hook — attach a breadcrumb drop target to a ref.
// Hook signature is load-bearing across consumers; PRESERVE verbatim.
// ---------------------------------------------------------------------------

function useBreadcrumbDropTarget(
    ref: React.RefObject<HTMLElement | null>,
    segment: IBreadcrumbSegment,
    onDrop: PickerBreadcrumbProps["onDrop"],
    isValidDrop: PickerBreadcrumbProps["isValidDrop"],
    onHoverChange: (folderId: number | null, hovered: boolean) => void,
    disabled?: boolean
) {
    const enabled =
        onDrop !== undefined && segment.folderId !== undefined && !disabled;

    useEffect(() => {
        if (!enabled || !ref.current || onDrop === undefined) return;
        if (segment.folderId === undefined) return;
        // Root segment carries `null`; all four callbacks accept `number | null`.
        // The hook does NOT narrow folderId to `number`; preserve that boundary.
        const targetId: number | null = segment.folderId;

        return dropTargetForElements({
            element: ref.current,
            canDrop: ({ source }) => {
                const items = source.data.allItems as ILibraryDragItem[];
                if (!items?.length) return false;
                return isValidDrop ? isValidDrop(targetId, items) : true;
            },
            onDragEnter: () => onHoverChange(targetId, true),
            onDragLeave: () => onHoverChange(targetId, false),
            onDrop: ({ source }) => {
                const items = source.data.allItems as ILibraryDragItem[];
                if (items?.length) {
                    onDrop(targetId, segment.label, items);
                }
                onHoverChange(targetId, false);
            },
        });
    }, [
        enabled,
        ref,
        segment.folderId,
        segment.label,
        onDrop,
        isValidDrop,
        onHoverChange,
    ]);
}

// ---------------------------------------------------------------------------
// Inline segment — past or current
// ---------------------------------------------------------------------------

const DroppableSegment = ({
    segment,
    onDrop,
    isValidDrop,
    isHovered,
    onHoverChange,
}: {
    segment: IBreadcrumbSegment;
    onDrop?: PickerBreadcrumbProps["onDrop"];
    isValidDrop?: PickerBreadcrumbProps["isValidDrop"];
    isHovered: boolean;
    onHoverChange: (folderId: number | null, hovered: boolean) => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);

    useBreadcrumbDropTarget(
        ref,
        segment,
        onDrop,
        isValidDrop,
        onHoverChange,
        segment.isCurrent
    );

    if (segment.isCurrent) {
        return (
            <div ref={ref} className="inline-flex">
                <span
                    aria-current="page"
                    // Truncation contract: max-width + truncate + title, so a
                    // verbose label (e.g. a long client name as the workspace
                    // root crumb, Unit 7) stays hoverable at full length.
                    title={segment.label}
                    className={cn(
                        "text-foreground max-w-[200px] truncate px-1 text-[0.8rem] font-semibold transition-colors duration-150",
                        isHovered && DROP_HOVER_CLASS
                    )}
                >
                    {segment.label}
                </span>
            </div>
        );
    }

    return (
        <div ref={ref} className="inline-flex">
            <button
                type="button"
                onClick={segment.onClick}
                title={segment.label}
                className={cn(
                    "text-primary hover:bg-primary/5 hover:text-primary/90 max-w-[160px] truncate rounded-md px-1.5 py-[3px] text-[0.8rem] font-medium transition-colors duration-150",
                    isHovered && DROP_HOVER_CLASS
                )}
            >
                {segment.label}
            </button>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Overflow listbox item
// ---------------------------------------------------------------------------

const DroppableOverflowItem = ({
    segment,
    onDrop,
    isValidDrop,
    isHovered,
    onHoverChange,
    onClickNav,
}: {
    segment: IBreadcrumbSegment;
    onDrop?: PickerBreadcrumbProps["onDrop"];
    isValidDrop?: PickerBreadcrumbProps["isValidDrop"];
    isHovered: boolean;
    onHoverChange: (folderId: number | null, hovered: boolean) => void;
    onClickNav: () => void;
}) => {
    const ref = useRef<HTMLLIElement>(null);
    useBreadcrumbDropTarget(ref, segment, onDrop, isValidDrop, onHoverChange);

    return (
        <li ref={ref} role="option" aria-selected={false}>
            <button
                type="button"
                onClick={onClickNav}
                title={segment.label}
                className={cn(
                    "text-foreground hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                    isHovered && DROP_HOVER_CLASS
                )}
            >
                <Folder size={14} fill="currentColor" className="shrink-0" />
                <span className="truncate">{segment.label}</span>
            </button>
        </li>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const folderKey = (folderId: number | null) =>
    folderId === null ? "__root__" : String(folderId);

const Separator = () => (
    <ChevronRight size={14} className="text-muted-foreground/40 mx-1" />
);

export const PickerBreadcrumb = ({
    segments,
    maxVisible,
    onDrop,
    isValidDrop,
}: PickerBreadcrumbProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    const [triggerDragHovered, setTriggerDragHovered] = useState(false);

    const overflowContainerRef = useRef<HTMLDivElement>(null);
    const overflowTriggerRef = useRef<HTMLDivElement>(null);
    const isDragOpenRef = useRef(false);

    const shouldCollapse =
        maxVisible !== undefined &&
        segments.length > maxVisible &&
        segments.length > 3;

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
        setTriggerDragHovered(false);
        isDragOpenRef.current = false;
    }, []);

    // Close when drag ends (anywhere) so the auto-open menu doesn't linger.
    useEffect(() => {
        if (!onDrop) return;
        return monitorForElements({
            onDrop: () => {
                if (isDragOpenRef.current) closeMenu();
                setHoveredKey(null);
            },
        });
    }, [onDrop, closeMenu]);

    // Open menu when a drag enters the … trigger. Because the menu is
    // NON-portalled (it lives inside `overflowContainerRef`), dragging from
    // the trigger DOWN into the listbox does NOT fire onDragLeave on the
    // trigger element — the listbox items are descendants. This is the
    // load-bearing property the bead is enforcing.
    useEffect(() => {
        if (!onDrop || !overflowTriggerRef.current) return;

        return dropTargetForElements({
            element: overflowTriggerRef.current,
            canDrop: () => true,
            onDragEnter: () => {
                isDragOpenRef.current = true;
                setTriggerDragHovered(true);
                setMenuOpen(true);
            },
            onDragLeave: () => {
                closeMenu();
            },
            onDrop: () => {},
        });
    }, [onDrop, closeMenu, shouldCollapse]);

    // Close on outside click (except while a drag is in progress — the drag's
    // `monitorForElements` watcher handles cleanup on real drop/cancel).
    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e: MouseEvent) => {
            if (isDragOpenRef.current) return;
            if (!overflowContainerRef.current?.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [menuOpen]);

    useEffect(() => {
        return () => {
            isDragOpenRef.current = false;
        };
    }, []);

    const handleHoverChange = useCallback(
        (folderId: number | null, hovered: boolean) => {
            setHoveredKey((prev) =>
                hovered
                    ? folderKey(folderId)
                    : prev === folderKey(folderId)
                      ? null
                      : prev
            );
        },
        []
    );

    const isSegmentHovered = (segment: IBreadcrumbSegment) =>
        hoveredKey !== null &&
        segment.folderId !== undefined &&
        folderKey(segment.folderId) === hoveredKey;

    if (segments.length === 0) return null;

    if (shouldCollapse) {
        const first = segments[0];
        const hidden = segments.slice(1, -2);
        const tail = segments.slice(-2);

        return (
            <nav
                data-breadcrumb-root
                aria-label="Breadcrumb"
                className="relative flex min-h-8 items-center gap-0.5 py-0.5 pl-0.5"
            >
                <ol className="flex items-center">
                    <li className="flex items-center">
                        <DroppableSegment
                            segment={first}
                            onDrop={onDrop}
                            isValidDrop={isValidDrop}
                            isHovered={isSegmentHovered(first)}
                            onHoverChange={handleHoverChange}
                        />
                    </li>

                    <li className="flex items-center" aria-hidden="true">
                        <Separator />
                    </li>

                    <li className="flex items-center">
                        <div
                            ref={overflowContainerRef}
                            className="relative inline-flex items-center"
                        >
                            <div ref={overflowTriggerRef}>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label="Show hidden segments"
                                    aria-haspopup="listbox"
                                    aria-expanded={menuOpen}
                                    onClick={() => {
                                        isDragOpenRef.current = false;
                                        setMenuOpen((prev) => !prev);
                                    }}
                                    className={cn(
                                        "text-muted-foreground hover:text-foreground h-6 w-6 transition-colors",
                                        triggerDragHovered && DROP_HOVER_CLASS
                                    )}
                                >
                                    <MoreHorizontal className="size-3.5" />
                                </Button>
                            </div>

                            {menuOpen && (
                                <ul
                                    role="listbox"
                                    className="bg-popover border-border absolute top-full left-0 z-50 mt-1 max-w-[280px] min-w-[200px] rounded-md border p-1 shadow-md"
                                >
                                    {hidden.map((crumb, i) =>
                                        onDrop ? (
                                            <DroppableOverflowItem
                                                key={i}
                                                segment={crumb}
                                                onDrop={onDrop}
                                                isValidDrop={isValidDrop}
                                                isHovered={isSegmentHovered(
                                                    crumb
                                                )}
                                                onHoverChange={
                                                    handleHoverChange
                                                }
                                                onClickNav={() => {
                                                    closeMenu();
                                                    crumb.onClick();
                                                }}
                                            />
                                        ) : (
                                            <li
                                                key={i}
                                                role="option"
                                                aria-selected={false}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        closeMenu();
                                                        crumb.onClick();
                                                    }}
                                                    className="text-foreground hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors"
                                                >
                                                    <Folder
                                                        size={14}
                                                        fill="currentColor"
                                                        className="shrink-0"
                                                    />
                                                    <span className="truncate">
                                                        {crumb.label}
                                                    </span>
                                                </button>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    </li>

                    {tail.map((crumb, i) => (
                        <li key={i} className="flex items-center">
                            <Separator />
                            <DroppableSegment
                                segment={crumb}
                                onDrop={onDrop}
                                isValidDrop={isValidDrop}
                                isHovered={isSegmentHovered(crumb)}
                                onHoverChange={handleHoverChange}
                            />
                        </li>
                    ))}
                </ol>
            </nav>
        );
    }

    return (
        <nav
            data-breadcrumb-root
            aria-label="Breadcrumb"
            className="flex min-h-8 items-center gap-0.5 py-0.5 pl-0.5"
        >
            <ol className="flex items-center">
                {segments.map((segment, index) => (
                    <li key={index} className="flex items-center">
                        {index > 0 && <Separator />}
                        <DroppableSegment
                            segment={segment}
                            onDrop={onDrop}
                            isValidDrop={isValidDrop}
                            isHovered={isSegmentHovered(segment)}
                            onHoverChange={handleHoverChange}
                        />
                    </li>
                ))}
            </ol>
        </nav>
    );
};
